import axios from 'axios'
import { get } from 'svelte/store'
import { server_path, user_storefronts, user_storefronts_loading_state, detected_fonts, user, text_entered, previews_tracking, previews_leading, previews_font_size, previews_sort_order, flash_content } from '../stores'
import { find, uniqBy, differenceBy } from 'lodash'

let maxFileSize = 5 * 1024 * 1024; // 5MB in bytes

// Load storefronts by fetching, normalizing, and mapping to fonts
export async function loadStorefronts() {
  console.log('Storefronts - loading storefronts')
  
  try {
    // Fetch storefronts from server
    let response = await axios.get(get(server_path) + 'storefronts/get.php', { withCredentials: true })
    
    if (response.data && response.data.success) {
      console.log('Storefronts - fetched successfully')
      
      // Normalize the data
      const normalizedStorefronts = normalizeStorefrontsData(response.data.data || [])
      
      // Set to store
      user_storefronts.set(normalizedStorefronts)

      // set loading state to success
      user_storefronts_loading_state.setSuccess()
      
      // Map to fonts
      mapStorefrontsDataToFonts(normalizedStorefronts)
      
    } else {
      user_storefronts_loading_state.setError(response.data.error || 'Unknown error')
    }
  } catch (error) {
    user_storefronts_loading_state.setError('Network error')
    console.error('Error loading storefronts:', error)
  }
}

// Normalize storefront data by parsing numeric values
function normalizeStorefrontsData(storefronts) {
  return storefronts.map(storefront => {
    return {
      ...storefront,
      tracking: parseFloat(storefront.tracking),
      line_space: parseFloat(storefront.line_space),
      font_scale: parseFloat(storefront.font_scale),
     };
  });
}

// Map storefronts to fonts in the detected fonts store
function mapStorefrontsDataToFonts(storefronts_data) {
  try {
    const detectedFonts = [...get(detected_fonts)]

    // Reset all storefronts in fonts
    detectedFonts.forEach(font => {
      font.storefronts = undefined
      font.storefront = false
    })

    storefronts_data.forEach(storefront => {
      const storefront_id = storefront.id
      const storefront_fonts = JSON.parse(storefront.fonts)

      storefront_fonts.forEach(storefront_font => {
        const fontIndex = detectedFonts.findIndex(
          f => f.postscript_name === storefront_font.postscript_name
        )

        if (fontIndex !== -1) {
          // Get existing storefronts array or create new one
          const existingStorefronts = detectedFonts[fontIndex].storefronts || [];
          
          // Remove any existing entry for this storefront
          const filteredStorefronts = existingStorefronts.filter(sf => sf.id !== storefront_id);
          
          // Add new storefront entry
          const updatedStorefronts = [
            ...filteredStorefronts,
            {
              id: storefront_id,
              preferred_name: storefront_font.preferred_name
            }
          ];

          // Update font object
          detectedFonts[fontIndex] = {
            ...detectedFonts[fontIndex],
            storefronts: updatedStorefronts,
            storefront: true
          }
        }
      })
    })

    detected_fonts.set(detectedFonts)
    console.log('Storefronts - mapped storefronts to fonts')
  } catch (error) {
    console.error('Error mapping storefronts to fonts:', error)
    throw error
  }
}

// Save a storefront to the server and reload storefronts
export async function saveStorefront(storefront_data) {
  console.log('Storefronts - saving storefront')
  let response = await axios.post(get(server_path) + 'storefronts/save.php', storefront_data, { withCredentials: true })
  if (response.data.success) {
    console.log('Storefronts - saved successfully')
    // Removed the call to loadStorefronts here
  } else {
    console.log('Storefronts - save failed', response.data.error)
  }
  return response
}

// Delete a storefront from the server and reload storefronts
export async function deleteStorefront(storefront_id) {
  console.log('Storefronts - deleting storefront', storefront_id)
  let form_data = new FormData()
  form_data.append('storefront_id', storefront_id)
  
  let response = await axios.post(get(server_path) + 'storefronts/delete.php', form_data, { withCredentials: true })
  
  if (response.data.success) {
    console.log('Storefronts - deleted successfully')
    await loadStorefronts()
  } else {
    console.error('Storefronts - delete failed:', response.data.error)
  }
  return response
}

// Create or update a storefront with the given name, fonts to add, fonts to remove, and optionally view options
export async function saveStorefrontData({ name, fonts_to_add = [], fonts_to_remove = [], fonts_to_rename = [], save_view_options, storefront_id, description, default_text }) {
  console.log('Storefronts Service: saveStorefrontData - received fonts_to_add:', JSON.parse(JSON.stringify(fonts_to_add)), 'storefront_id:', storefront_id, 'name:', name);
  // -------------------------
  // Begin preparing form data
  let form_data = new FormData()
  form_data.append('user_id', get(user).id)
  if (storefront_id) form_data.append('storefront_id', storefront_id)
  if (name) form_data.append('name', name)
  if (description) form_data.append('description', description)
  if (default_text) form_data.append('default_text', default_text)

  // Prepare fonts array
  let final_fonts = []
  
  // If we're updating an existing storefront
  if (storefront_id) {
    const existing_storefront = find(get(user_storefronts), { id: storefront_id })
    if (existing_storefront) {
      // Start with existing fonts
      final_fonts = JSON.parse(existing_storefront.fonts)
      
      // Remove fonts if any were specified
      if (fonts_to_remove.length > 0) {
        final_fonts = differenceBy(final_fonts, fonts_to_remove, 'postscript_name')
      }
      
      // Handle font renaming
      if (fonts_to_rename.length > 0) {
        fonts_to_rename.forEach(renameFont => {
          const fontIndex = final_fonts.findIndex(f => f.postscript_name === renameFont.postscript_name)
          if (fontIndex >= 0) {
            final_fonts[fontIndex].preferred_name = renameFont.preferred_name
          }
        })
      }
      
      // Merge new fonts with existing ones
      if (fonts_to_add.length > 0) {
        fonts_to_add.forEach(newFont => {
          const existingFontIndex = final_fonts.findIndex(f => f.postscript_name === newFont.postscript_name)
          if (existingFontIndex >= 0) {
            const fontData = {
              postscript_name: newFont.postscript_name,
              preferred_name: newFont.preferred_name || newFont.display_name || newFont.postscript_name,
              file_name: newFont.file_name
            }
            final_fonts[existingFontIndex] = fontData
          } else {
            const fontData = {
              postscript_name: newFont.postscript_name,
              preferred_name: newFont.preferred_name || newFont.display_name || newFont.postscript_name,
              file_name: newFont.file_name  
            }
            final_fonts.push(fontData)
          }
        })
      }
    }
  } else if (fonts_to_add.length > 0) {
    // For new storefronts, just use fonts_to_add
    final_fonts = fonts_to_add.map(font => {
      const fontData = {
        postscript_name: font.postscript_name,
        preferred_name: font.preferred_name || font.display_name || font.postscript_name,
        file_name: font.file_name  
      }
      return fontData
    })
  }

  // Always append fonts array, even if empty
  form_data.append('fonts', JSON.stringify(final_fonts))

  // Append view options if specified
  if (save_view_options) {
    form_data.append('tracking', get(previews_tracking))
    form_data.append('line_space', get(previews_leading))
    form_data.append('font_scale', get(previews_font_size))
    form_data.append('sort_order', get(previews_sort_order))
  }

  // -------------------------
  // End preparing form data

  // Save the storefront and wait for storefront to be mapped to fonts before returning
  const response = await saveStorefront(form_data)
  if (response.data.success) {
    await loadStorefronts(); 
  }
  return {
    data: {
      success: response.data.success,
      ...(response.data.success ? { storefront: response.data.storefront } : { error: response.data.error })
    }
  }
}

// Get a storefront by url hash
export async function getStorefrontByHash(hash) {
  if (!hash) {
    return { 
      success: false, 
      message: 'URL hash is required' 
    };
  }

  const response = await axios.get(get(server_path) + 'storefronts/get_by_hash.php', {
    params: { hash },
    withCredentials: true
  });
  return response.data;
}

// Reload storefronts by fetching and setting them
export async function reloadStorefronts() {
  // reset storefronts in fonts
  get(detected_fonts).forEach(font => {
    font.storefronts = undefined
    font.storefront = false
  })
  // load storefronts
  try {
    await loadStorefronts()
  } catch (error) {
    console.error('Error reloading storefronts:', error)
  }
}